import { useEffect, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { queryClient } from './api/queryClient.js';
import { useShallow } from 'zustand/react/shallow';
import ErrorBoundary from './app/ErrorBoundary.jsx';
import Toaster from './components/Toaster.jsx';
import { routes } from './routes.jsx';
import { useSettings, applyAppearance } from './app/settingsStore.js';
import { bootstrap } from './app/bootstrap.js';
import SettingsJoinPrompt from './features/settings/SettingsJoinPrompt.jsx';

export default function App() {
  const router = useMemo(() => createBrowserRouter(routes), []);
  const appearance = useSettings(
    useShallow((s) => ({
      theme: s.theme,
      accent: s.accent,
      textSize: s.textSize,
      cardFont: s.cardFont,
      cardAlign: s.cardAlign,
      motion: s.motion,
      density: s.density,
      width: s.width,
    })),
  );

  useEffect(() => {
    applyAppearance(appearance);
    if (appearance.theme !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => applyAppearance(appearance);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [appearance]);

  useEffect(() => {
    bootstrap();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <RouterProvider router={router} />
        <SettingsJoinPrompt />
        <Toaster />
      </ErrorBoundary>
    </QueryClientProvider>
  );
}
